import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import SVG from "react-inlinesvg";
import { getSidebarData } from "./SidebarData";
import { toAbsoluteUrl } from "../../../_helpers";
import { useSelector, useDispatch } from "react-redux";
import { getBaseImageURL } from "../../../../helpers";
import { getPermission } from "../../../../store/actions/roleManagement/roleActions";
import * as actions from "../../../../store/actions/index";
import "./Sidebar.scss";

const Sidebar = ({ collapsed, setCollapsed, mobileMenu }) => {
  const [activeParent, setActiveParent] = useState(null);
  const [activeChild, setActiveChild] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [logo, setLogo] = useState();
  const globalImagePath = useRef(getBaseImageURL());

  const { siteLogo } = useSelector((state) => state.settings?.generalSettings);
  const { currentPermissions } = useSelector((state) => state.role);
  const { generalSettings } = useSelector((state) => state.settings);
  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const { manageOptionsLanguages } = useSelector(
    (state) => state.manageOptions
  );

  // Fetch permissions on mount
  useEffect(() => {
    dispatch(getPermission(localStorage.getItem("role")));
    dispatch(actions.getManageOptionsLanguages());
  }, [dispatch]);

  const toggle = () => setCollapsed(!collapsed);

  useEffect(() => {
    if (siteLogo) {
      setLogo(siteLogo);
    }
  }, [siteLogo]);

  const sidebarData = useMemo(() => {
    return getSidebarData(currentPermissions, generalSettings, taxonomy);
  }, [currentPermissions, generalSettings, taxonomy]);

  const isPathActive = (path) => {
    const currentPath = location.pathname;
    return currentPath === path || currentPath.startsWith(path + "/");
  };

  useEffect(() => {
    const currentPath = location.pathname;

    sidebarData.forEach((item, parentIdx) => {
      if (item.path === currentPath) {
        setActiveParent(parentIdx);
        setActiveChild(null);
        return;
      }

      let foundActiveChild = false;
      item.subItems?.forEach((subItem, subIdx) => {
        if (isPathActive(subItem.path)) {
          setActiveParent(parentIdx);
          setActiveChild(`${parentIdx}-${subIdx}`);
          foundActiveChild = true;
        }
      });

      if (
        !foundActiveChild &&
        item.subItems.length > 0 &&
        currentPath.startsWith(item.path + "/")
      ) {
        setActiveParent(parentIdx);
      }
    });
  }, [location.pathname, sidebarData]);

  const setLanguageOnSubMenuChange = () => {
    dispatch(
      actions.setManageOptionsCurrentId(
        manageOptionsLanguages?.find((lng) => lng.isDefault === true)?.id
      )
    );
  };

  const handleParentClick = (item, idx) => {
    if (item.subItems.length === 0) {
      navigate(item.path);
      setActiveParent(idx);
      setActiveChild(null);
    } else {
      setActiveParent(activeParent === idx ? null : idx);
    }
  };

  const handleSubItemClick = (subItem, parentIdx, subIdx) => {
    // Check if language reset is needed for specific routes
    const languageResetPaths = [
      "/campaign/categories",
      "/content-page/pages",
      "/content-page/investor-contracts",
      "/content-page/menu",
      "/content-page/faqs",
      "/content-page/front-end-faqs",
      "/manage-options/company-industry",
      "/manage-options/team-member-type",
      "/manage-options/investor-type",
      "/manage-options/campaign-stage",
      "/manage-options/investing-round",
      "/miscellaneous/sliders",
      "/settings/meta",
    ];

    if (languageResetPaths.includes(subItem.path)) {
      setLanguageOnSubMenuChange();
    }

    navigate(subItem.path);
    setActiveChild(`${parentIdx}-${subIdx}`);
  };

  const handleMouseEnter = () => {
    if (collapsed) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Determine if sidebar should appear expanded
  const isExpanded = !collapsed || isHovered;

  const ChevronDownIcon = ({ isOpen }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`chevron-icon ${isOpen ? "open" : ""}`}>
      <polyline
        points="6,9 12,15 18,9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div
      className={`sidebar-wrapper ${mobileMenu && "active"} ${
        collapsed ? "collapsed" : ""
      } ${isHovered ? "hovered" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {/* Header */}
      <div
        className={`sidebar-header ${
          !isExpanded ? "justify-content-center" : "justify-content-between"
        }`}>
        {isExpanded && (
          <Link to="/" className="logo ms-4">
            <img
              alt="logo"
              src={`${globalImagePath.current}${logo}`}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = toAbsoluteUrl(
                  "media/misc/placeholder.png"
                );
              }}
            />
          </Link>
        )}
        <button className="toggle-btn text-dark p-0" onClick={toggle}>
          <SVG
            src={toAbsoluteUrl("media/svg/icons/Sidebar/toggle.svg")}
            style={{
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="d-flex flex-column thin-scrollbar sidebar-nav">
        {sidebarData.map((item, idx) => {
          const isParentActive = activeParent === idx;
          const hasSubItems = item.subItems.length > 0;

          const isParentRouteActive =
            location.pathname === item.path ||
            item.subItems?.some((sub) => isPathActive(sub.path));

          return (
            <div
              className={`sidebar-nav-item-wrapper ${
                !isExpanded ? "ps-0" : "ps-5 pe-3"
              } `}
              key={idx}>
              <div
                className={`sidebar-nav-link d-flex align-items-center mb-1 px-2 ${
                  isParentRouteActive ? "sidebar-link-active" : ""
                }`}
                onClick={() => handleParentClick(item, idx)}
                title={!isExpanded ? item.title : ""}>
                <div className="icon-container">
                  <SVG
                    src={toAbsoluteUrl(item.iconPath)}
                    alt={`${item.title} icon`}
                    className="nav-icon"
                  />
                </div>

                <p className="nav-text">{item.title}</p>
                {hasSubItems && (
                  <div className="chevron-icon-wrapper">
                    <ChevronDownIcon isOpen={isParentActive} />
                  </div>
                )}
              </div>

              {hasSubItems && isExpanded && (
                <div
                  className={`sidebar-submenu-wrapper ${
                    isParentActive ? "open" : ""
                  }`}>
                  <ul className="sidebar-submenu">
                    {item.subItems.map((sub, subIdx) => (
                      <li
                        key={subIdx}
                        className={`sidebar-submenu-item ${
                          activeChild === `${idx}-${subIdx}` ||
                          isPathActive(sub.path)
                            ? "sidebar-link-active"
                            : ""
                        }`}
                        onClick={() => handleSubItemClick(sub, idx, subIdx)}>
                        {sub.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
